import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';

const api = axios.create({ baseURL: '/api/imagegenerator', headers: { 'Content-Type': 'application/json' } });

type AspectRatio = 'LANDSCAPE' | 'PORTRAIT' | 'SQUARE';
type TabKey = 'generate' | 'reference' | 'gallery';
type Provider = 'whisk' | 'imagen';

const ASPECT_OPTIONS: { value: AspectRatio; label: string; icon: string; desc: string }[] = [
  { value: 'LANDSCAPE', label: 'Landscape', icon: '⬜', desc: 'Horizontal (wide)' },
  { value: 'PORTRAIT', label: 'Portrait', icon: '🔲', desc: 'Vertical (tall)' },
  { value: 'SQUARE', label: 'Square', icon: '⏹', desc: '1:1 ratio' },
];

interface GeneratedImage {
  id: number;
  project_id: number;
  prompt: string;
  aspect_ratio: string;
  file_name: string;
  file_size_kb: number;
  mime_type: string;
  seed: number;
  media_id: string | null;
  is_refined: boolean;
  parent_image_id: number | null;
  refine_prompt: string | null;
  created_at: string;
  image_base64?: string;
}

export default function ImageGeneratorPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  // Tab state
  const [activeTab, setActiveTab] = useState<TabKey>('generate');

  // Cookie state
  const [hasCookie, setHasCookie] = useState(false);
  const [cookiePreview, setCookiePreview] = useState('');
  const [cookieInput, setCookieInput] = useState('');
  const [cookieExpanded, setCookieExpanded] = useState(true);
  const [cookieLoading, setCookieLoading] = useState(false);
  const [cookieError, setCookieError] = useState('');

  // Provider state
  const [provider, setProvider] = useState<Provider>('whisk');
  const [imagenApiKey, setImagenApiKey] = useState('');

  // Generate state
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('LANDSCAPE');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Reference image state
  const [refFile, setRefFile] = useState<File | null>(null);
  const [refPreview, setRefPreview] = useState<string | null>(null);
  const [refPrompt, setRefPrompt] = useState('');
  const [refAspect, setRefAspect] = useState<AspectRatio>('LANDSCAPE');
  const [refGenerating, setRefGenerating] = useState(false);
  const [refError, setRefError] = useState('');
  const [refCaptions, setRefCaptions] = useState<string[]>([]);
  const [captionLoading, setCaptionLoading] = useState(false);
  const refInputRef = useRef<HTMLInputElement>(null);

  // Result state (current generation result)
  const [result, setResult] = useState<GeneratedImage & { image_base64: string } | null>(null);

  // Gallery state
  const [gallery, setGallery] = useState<GeneratedImage[]>([]);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<(GeneratedImage & { image_base64?: string }) | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  // Logo state
  const [hasLogo, setHasLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Article title (for featured image)
  const [articleTitle, setArticleTitle] = useState('');

  // Refine state
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState('');

  // Load cookie status + logo on mount / project change
  useEffect(() => {
    if (projectId) {
      loadCookieStatus();
      loadLogoStatus();
    }
  }, [projectId]);

  // Load gallery when switching to gallery tab
  useEffect(() => {
    if (activeTab === 'gallery' && projectId) loadGallery();
  }, [activeTab, projectId]);

  const loadCookieStatus = async () => {
    try {
      const resp = await api.get(`/cookie/${projectId}`);
      setHasCookie(resp.data.has_cookie);
      if (resp.data.cookie) {
        setCookiePreview(resp.data.cookie.cookie_preview || '');
        setCookieExpanded(false);
      } else {
        setCookieExpanded(true);
      }
    } catch {
      // Cookie not set yet
    }
  };

  const handleSaveCookie = async () => {
    if (!cookieInput.trim() || !projectId) return;
    setCookieLoading(true);
    setCookieError('');
    try {
      await api.post('/cookie', {
        project_id: Number(projectId),
        cookie_value: cookieInput.trim(),
      });
      setCookieInput('');
      await loadCookieStatus();
    } catch (err: any) {
      setCookieError(err.response?.data?.detail || 'Failed to save cookie');
    } finally {
      setCookieLoading(false);
    }
  };

  const handleDeleteCookie = async () => {
    if (!projectId) return;
    setCookieLoading(true);
    try {
      await api.delete(`/cookie/${projectId}`);
      setHasCookie(false);
      setCookiePreview('');
      setCookieExpanded(true);
    } catch {
      // ignore
    } finally {
      setCookieLoading(false);
    }
  };

  // ── Logo Management ────────────────────────────────────────────────

  const loadLogoStatus = async () => {
    try {
      const resp = await api.get(`/logo/${projectId}`);
      setHasLogo(resp.data.has_logo);
      if (resp.data.logo) {
        setLogoPreview(`data:image/png;base64,${resp.data.logo.image_base64}`);
        setLogoFileName(resp.data.logo.file_name);
      } else {
        setLogoPreview(null);
        setLogoFileName('');
      }
    } catch {
      // Logo not set yet
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('file', file);
      await axios.post('/api/imagegenerator/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadLogoStatus();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!projectId) return;
    try {
      await api.delete(`/logo/${projectId}`);
      setHasLogo(false);
      setLogoPreview(null);
      setLogoFileName('');
    } catch {
      // ignore
    }
  };

  // ── Text-to-Image Generate ──────────────────────────────────────────

  const handleGenerate = async () => {
    if (!prompt.trim() || !projectId) return;
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const body: any = {
        project_id: Number(projectId),
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
        provider,
      };
      if (provider === 'imagen' && imagenApiKey.trim()) {
        body.api_key = imagenApiKey.trim();
      }
      if (articleTitle.trim()) {
        body.article_title = articleTitle.trim();
      }
      const resp = await api.post('/generate', body);
      setResult(resp.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Image generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ── Reference Image Generate ────────────────────────────────────────

  const handleRefFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRefFile(file);
    setRefCaptions([]);
    const reader = new FileReader();
    reader.onload = () => setRefPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleAnalyzeRef = async () => {
    if (!refFile || !projectId) return;
    setCaptionLoading(true);
    setRefError('');
    setRefCaptions([]);
    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('count', '3');
      formData.append('provider', provider);
      if (provider === 'imagen' && imagenApiKey.trim()) {
        formData.append('api_key', imagenApiKey.trim());
      }
      formData.append('file', refFile);
      const resp = await axios.post('/api/imagegenerator/caption', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRefCaptions(resp.data.captions || []);
    } catch (err: any) {
      setRefError(err.response?.data?.detail || 'Failed to analyze image');
    } finally {
      setCaptionLoading(false);
    }
  };

  const handleGenerateFromRef = async () => {
    if (!refFile || !refPrompt.trim() || !projectId) return;
    setRefGenerating(true);
    setRefError('');
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('prompt', refPrompt.trim());
      formData.append('aspect_ratio', refAspect);
      formData.append('seed', '0');
      formData.append('provider', provider);
      if (provider === 'imagen' && imagenApiKey.trim()) {
        formData.append('api_key', imagenApiKey.trim());
      }
      if (articleTitle.trim()) {
        formData.append('article_title', articleTitle.trim());
      }
      formData.append('file', refFile);
      const resp = await axios.post('/api/imagegenerator/generate-from-reference', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(resp.data);
      setActiveTab('generate'); // Switch to generate tab to show result
    } catch (err: any) {
      setRefError(err.response?.data?.detail || err.message || 'Generation from reference failed');
    } finally {
      setRefGenerating(false);
    }
  };

  // ── Gallery ─────────────────────────────────────────────────────────

  const loadGallery = async () => {
    if (!projectId) return;
    setGalleryLoading(true);
    try {
      const resp = await api.get(`/images/${projectId}?limit=50`);
      setGallery(resp.data.images || []);
      setGalleryTotal(resp.data.total || 0);
    } catch {
      // ignore
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleViewImage = async (imageId: number) => {
    setImageLoading(true);
    setSelectedImage(null);
    try {
      const resp = await api.get(`/image/${imageId}`);
      setSelectedImage(resp.data);
    } catch {
      // ignore
    } finally {
      setImageLoading(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!confirm('Delete this image permanently?')) return;
    try {
      await api.delete(`/image/${imageId}`);
      setGallery(prev => prev.filter(img => img.id !== imageId));
      setGalleryTotal(prev => prev - 1);
      if (selectedImage?.id === imageId) setSelectedImage(null);
    } catch {
      // ignore
    }
  };

  // ── Refine ──────────────────────────────────────────────────────────

  const handleRefine = async () => {
    if (!selectedImage || !refinePrompt.trim() || !projectId) return;
    setRefining(true);
    setRefineError('');
    try {
      const resp = await api.post('/refine', {
        project_id: Number(projectId),
        image_id: selectedImage.id,
        refine_prompt: refinePrompt.trim(),
      });
      setResult(resp.data);
      setRefinePrompt('');
      setSelectedImage(null);
      setActiveTab('generate'); // Show the refined result
      loadGallery(); // Refresh gallery
    } catch (err: any) {
      setRefineError(err.response?.data?.detail || 'Refinement failed');
    } finally {
      setRefining(false);
    }
  };

  // ── Download ────────────────────────────────────────────────────────

  const handleDownload = (imageData?: { mime_type: string; image_base64: string; file_name: string }) => {
    const data = imageData || result;
    if (!data?.image_base64) return;
    const link = document.createElement('a');
    link.href = `data:${data.mime_type};base64,${data.image_base64}`;
    link.download = data.file_name;
    link.click();
  };

  if (!projectId) {
    return (
      <Layout projectId={null}>
        <div className="max-w-3xl mx-auto py-12 text-center">
          <h2 className="text-xl font-semibold text-gray-700">No Project Selected</h2>
          <p className="text-gray-500 mt-2">Select a project from the Projects page first.</p>
        </div>
      </Layout>
    );
  }

  const TABS: { key: TabKey; label: string; icon: string }[] = [
    { key: 'generate', label: 'Text to Image', icon: '✨' },
    { key: 'reference', label: 'Reference Image', icon: '🖼' },
    { key: 'gallery', label: `Gallery${galleryTotal ? ` (${galleryTotal})` : ''}`, icon: '📁' },
  ];

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Image Generator</h1>
          <p className="text-gray-500 mt-1">
            Generate AI images using Whisk (IMAGEN 3.5) or Nano Banana 2 (Imagen 3 API). Upload reference images or generate from text.
                <a href="https://drive.google.com/file/d/1GnGaTAu1IfU6-qYs2-j75TvdlVprAJbm/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
          </p>
        </div>

        {/* Provider Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-medium text-gray-900">Image Provider</span>
            </div>

            {/* Provider Toggle */}
            <div className="flex gap-3 mb-4">
              <button
                onClick={() => setProvider('whisk')}
                className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  provider === 'whisk'
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                  provider === 'whisk' ? 'border-brand-500' : 'border-gray-300'
                }`}>
                  {provider === 'whisk' && <div className="w-1.5 h-1.5 rounded-full bg-brand-500" />}
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900">Whisk Cookie</div>
                  <div className="text-xs text-gray-500">Google IMAGEN 3.5 via Whisk CLI</div>
                </div>
              </button>
              <button
                onClick={() => setProvider('imagen')}
                className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  provider === 'imagen'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                  provider === 'imagen' ? 'border-purple-500' : 'border-gray-300'
                }`}>
                  {provider === 'imagen' && <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900">Nano Banana 2</div>
                  <div className="text-xs text-gray-500">Google Imagen 3 via API Key</div>
                </div>
              </button>
            </div>

            {/* Whisk Cookie Section */}
            {provider === 'whisk' && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${hasCookie ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className="text-sm font-medium text-gray-700">Cookie Authentication</span>
                  {hasCookie && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Connected</span>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
                  <p className="font-medium mb-2">How to get your cookie:</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-700">
                    <li>Go to <strong>labs.google/fx/tools/image-fx</strong> and sign in with your Google account</li>
                    <li>Open browser DevTools (F12) and go to the <strong>Network</strong> tab</li>
                    <li>Generate any image, then click the network request to <strong>GenerateImages</strong></li>
                    <li>In the request headers, copy the full <strong>Cookie</strong> header value</li>
                    <li>Paste it below and save</li>
                  </ol>
                </div>

                {hasCookie && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center justify-between">
                    <div>
                      <span className="text-sm text-gray-500">Current cookie: </span>
                      <code className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded">{cookiePreview}</code>
                    </div>
                    <button
                      onClick={handleDeleteCookie}
                      disabled={cookieLoading}
                      className="text-sm text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                )}

                <div className="flex gap-3">
                  <textarea
                    value={cookieInput}
                    onChange={(e) => setCookieInput(e.target.value)}
                    placeholder="Paste your cookie value here..."
                    rows={3}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y font-mono"
                  />
                  <button
                    onClick={handleSaveCookie}
                    disabled={!cookieInput.trim() || cookieLoading}
                    className="self-end px-5 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {cookieLoading ? 'Saving...' : 'Save Cookie'}
                  </button>
                </div>

                {cookieError && (
                  <p className="text-sm text-red-600 mt-2">{cookieError}</p>
                )}
              </div>
            )}

            {/* Imagen API Key Section */}
            {provider === 'imagen' && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-gray-700">API Key (Optional)</span>
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Default key configured</span>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 text-sm text-purple-800">
                  <p>A default Google AI Studio API key is already configured. You can optionally override it with your own key below.</p>
                </div>

                <div className="flex gap-3">
                  <input
                    type="password"
                    value={imagenApiKey}
                    onChange={(e) => setImagenApiKey(e.target.value)}
                    placeholder="Optional: paste your own Google AI Studio API key..."
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono"
                  />
                  {imagenApiKey && (
                    <button
                      onClick={() => setImagenApiKey('')}
                      className="px-4 py-2.5 rounded-lg text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Logo Upload */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-medium text-gray-900">Project Logo</span>
              {hasLogo && (
                <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Uploaded</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Upload a PNG logo to overlay on the top-left corner of every generated image.
            </p>

            <div className="flex items-center gap-4">
              {logoPreview && (
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <img src={logoPreview} alt="Logo" className="h-10 max-w-[120px] object-contain" />
                  <div>
                    <p className="text-xs text-gray-600">{logoFileName}</p>
                    <button
                      onClick={handleDeleteLogo}
                      className="text-xs text-red-500 hover:text-red-700 mt-0.5"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
              <div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {logoUploading ? 'Uploading...' : hasLogo ? 'Replace Logo' : 'Upload Logo (PNG)'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Text to Image ────────────────────────────────────────── */}
        {activeTab === 'generate' && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the image you want to generate..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
                <div className="flex gap-3">
                  {ASPECT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setAspectRatio(opt.value)}
                      className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                        aspectRatio === opt.value
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <div className="text-lg mb-1">{opt.icon}</div>
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Article Title <span className="text-gray-400 font-normal">(optional — for featured image)</span>
                </label>
                <input
                  type="text"
                  value={articleTitle}
                  onChange={(e) => setArticleTitle(e.target.value)}
                  placeholder="e.g., 10 Best Tips for Recovery in 2026"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p className="text-xs text-gray-400 mt-1">If provided, the title will be overlaid on the generated image.</p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim() || (provider === 'whisk' && !hasCookie)}
                className={`w-full py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 ${
                  provider === 'imagen' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {generating ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating image... (this can take 30-60 seconds)
                  </>
                ) : (
                  <>Generate Image ({provider === 'imagen' ? 'Nano Banana 2' : 'Whisk'})</>
                )}
              </button>

              {provider === 'whisk' && !hasCookie && (
                <p className="text-sm text-yellow-600 text-center">
                  Save a cookie above before generating images.
                </p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {result.is_refined ? 'Refined Image' : 'Generated Image'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {result.file_name} &middot; {result.file_size_kb} KB &middot; {result.aspect_ratio}
                      {result.is_refined && result.refine_prompt && (
                        <span className="ml-2 text-purple-600">Refined: "{result.refine_prompt}"</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDownload()}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download
                  </button>
                </div>
                <div className="p-6 flex justify-center">
                  <img
                    src={`data:${result.mime_type};base64,${result.image_base64}`}
                    alt={result.prompt}
                    className="max-w-full max-h-[600px] rounded-lg shadow-md"
                  />
                </div>
                <div className="px-6 pb-4">
                  <p className="text-xs text-gray-400 truncate">Prompt: {result.prompt}</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Tab: Reference Image ──────────────────────────────────────── */}
        {activeTab === 'reference' && (
          <div className="space-y-6">
            {/* Upload Reference */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-medium text-gray-900 mb-4">Upload Reference Image</h3>
              <p className="text-sm text-gray-500 mb-4">
                Upload an image as a reference. The AI will analyze it and combine it with your description to generate a new image.
              </p>

              <div className="flex gap-6">
                {/* Upload area */}
                <div className="flex-1">
                  <div
                    onClick={() => refInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      refPreview ? 'border-brand-300 bg-brand-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                  >
                    {refPreview ? (
                      <img src={refPreview} alt="Reference" className="max-h-48 mx-auto rounded-lg shadow" />
                    ) : (
                      <>
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm text-gray-500">Click to upload a reference image</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP up to 10MB</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={refInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleRefFileChange}
                    className="hidden"
                  />
                  {refFile && (
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-sm text-gray-600">{refFile.name}</span>
                      <button
                        onClick={handleAnalyzeRef}
                        disabled={captionLoading || (provider === 'whisk' && !hasCookie)}
                        className="text-sm px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {captionLoading ? 'Analyzing...' : 'Analyze Image'}
                      </button>
                      <button
                        onClick={() => { setRefFile(null); setRefPreview(null); setRefCaptions([]); }}
                        className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {/* Captions */}
                {refCaptions.length > 0 && (
                  <div className="flex-1 bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-indigo-800 mb-2">AI Analysis of Reference</h4>
                    <ul className="space-y-2">
                      {refCaptions.map((cap, i) => (
                        <li key={i} className="text-sm text-indigo-700 bg-white rounded-lg p-2 border border-indigo-100">
                          {cap}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => setRefPrompt(prev => prev ? prev + '\n' + refCaptions.join('; ') : refCaptions.join('; '))}
                      className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Use captions in prompt
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Generate from Reference */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
              <h3 className="font-medium text-gray-900">Describe What You Need</h3>
              <p className="text-sm text-gray-500">
                Tell the AI what kind of image you want, using the reference as a starting point.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Description</label>
                <textarea
                  value={refPrompt}
                  onChange={(e) => setRefPrompt(e.target.value)}
                  placeholder="e.g., Create a similar style image but with a sunset background and warmer colors..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
                <div className="flex gap-3">
                  {ASPECT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setRefAspect(opt.value)}
                      className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                        refAspect === opt.value
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <div className="text-lg mb-1">{opt.icon}</div>
                      <div className="text-sm font-medium">{opt.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleGenerateFromRef}
                disabled={refGenerating || !refFile || !refPrompt.trim() || (provider === 'whisk' && !hasCookie)}
                className={`w-full py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 ${
                  provider === 'imagen' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {refGenerating ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Analyzing reference &amp; generating... (may take 60-90 seconds)
                  </>
                ) : (
                  'Generate from Reference'
                )}
              </button>

              {provider === 'whisk' && !hasCookie && (
                <p className="text-sm text-yellow-600 text-center">Save a cookie first.</p>
              )}
              {!refFile && (
                <p className="text-sm text-gray-400 text-center">Upload a reference image above to get started.</p>
              )}

              {refError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{refError}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tab: Gallery ──────────────────────────────────────────────── */}
        {activeTab === 'gallery' && (
          <div className="space-y-6">
            {/* Gallery Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">
                Generated Images {galleryTotal > 0 && <span className="text-gray-400">({galleryTotal})</span>}
              </h3>
              <button
                onClick={loadGallery}
                disabled={galleryLoading}
                className="text-sm text-brand-600 hover:text-brand-700"
              >
                {galleryLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {galleryLoading && gallery.length === 0 ? (
              <div className="text-center py-12 text-gray-400">Loading images...</div>
            ) : gallery.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500">No images generated yet.</p>
                <p className="text-sm text-gray-400 mt-1">Generate your first image using the "Text to Image" or "Reference Image" tab.</p>
              </div>
            ) : (
              <>
                {/* Image Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {gallery.map((img) => (
                    <div
                      key={img.id}
                      onClick={() => handleViewImage(img.id)}
                      className={`bg-white rounded-xl border-2 overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
                        selectedImage?.id === img.id ? 'border-brand-500 shadow-lg' : 'border-gray-200'
                      }`}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        <img
                          src={`/api/imagegenerator/image/${img.id}/file?token=${localStorage.getItem('auth_token') || ''}`}
                          alt={img.prompt}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {img.is_refined && (
                          <span className="absolute top-2 right-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                            Refined
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-gray-700 truncate">{img.prompt}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[10px] text-gray-400">{img.aspect_ratio}</span>
                          <span className="text-[10px] text-gray-400">{img.file_size_kb} KB</span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(img.created_at).toLocaleDateString()} {new Date(img.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Selected Image Detail */}
                {imageLoading && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <svg className="animate-spin h-8 w-8 mx-auto text-brand-600" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="text-gray-500 mt-3">Loading image...</p>
                  </div>
                )}

                {selectedImage && selectedImage.image_base64 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900">
                          {selectedImage.is_refined ? 'Refined Image' : 'Generated Image'} #{selectedImage.id}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {selectedImage.file_name} &middot; {selectedImage.file_size_kb} KB &middot; {selectedImage.aspect_ratio}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleDownload(selectedImage as any)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => handleDeleteImage(selectedImage.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => setSelectedImage(null)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="p-6 flex justify-center">
                      <img
                        src={`data:${selectedImage.mime_type};base64,${selectedImage.image_base64}`}
                        alt={selectedImage.prompt}
                        className="max-w-full max-h-[500px] rounded-lg shadow-md"
                      />
                    </div>

                    <div className="px-6 pb-4 space-y-2">
                      <p className="text-xs text-gray-500">
                        <span className="font-medium text-gray-600">Prompt:</span> {selectedImage.prompt}
                      </p>
                      {selectedImage.is_refined && selectedImage.refine_prompt && (
                        <p className="text-xs text-purple-600">
                          <span className="font-medium">Refine prompt:</span> {selectedImage.refine_prompt}
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        Created: {new Date(selectedImage.created_at).toLocaleString()}
                        {selectedImage.media_id && <span> &middot; Media ID: {selectedImage.media_id}</span>}
                      </p>
                    </div>

                    {/* Refine Section */}
                    {selectedImage.media_id && (
                      <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Edit / Refine This Image</h4>
                        <div className="flex gap-3">
                          <input
                            type="text"
                            value={refinePrompt}
                            onChange={(e) => setRefinePrompt(e.target.value)}
                            placeholder="e.g., Make it snowy, Add a red hat, Change background to sunset..."
                            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <button
                            onClick={handleRefine}
                            disabled={refining || !refinePrompt.trim()}
                            className="px-5 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                          >
                            {refining ? 'Refining...' : 'Refine Image'}
                          </button>
                        </div>
                        {refineError && (
                          <p className="text-sm text-red-600 mt-2">{refineError}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
